import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

declare var maplibregl: any;

interface MapClickEvent {
  lngLat: { lng: number; lat: number };
  point: { x: number; y: number };
}

interface MarkerOptions {
  id: string;
  lng: number;
  lat: number;
  popup?: string;
  color?: string;
  draggable?: boolean;
}

@Component({
  selector: 'app-maplibre-gl',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './maplibre-gl.component.html',
  styleUrls: ['./maplibre-gl.component.scss']
})
export class MaplibreGlComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;
  
  @Input() style: string | any = {
    version: 8,
    sources: {
      'base-tiles': {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [
      {
        id: 'base-layer',
        type: 'raster',
        source: 'base-tiles',
        minzoom: 0,
        maxzoom: 19
      }
    ]
  };
  @Input() center: [number, number] = [69.2401, 41.2995]; 
  @Input() zoom: number = 13;
  @Input() pitch: number = 0;
  @Input() bearing: number = 0;
  @Input() height: string = '400px';
  @Input() markers: MarkerOptions[] = [];
  @Input() navigationControl: boolean = true;
  @Input() geolocateControl: boolean = false;
  @Input() scaleControl: boolean = true;
  @Input() fullscreenControl: boolean = false;
  @Input() showWeatherControls: boolean = false;
  
  @Output() mapClick = new EventEmitter<MapClickEvent>();
  @Output() mapLoad = new EventEmitter<any>();
  @Output() markerClick = new EventEmitter<MarkerOptions>();
  @Output() mapMove = new EventEmitter<{ center: [number, number]; zoom: number }>();
  
  private map: any;
  private mapMarkers: Map<string, any> = new Map();
  isMapInitialized: boolean = false;
  activeWeatherLayer: string | null = null;
  loadingWeatherLayer: boolean = false;
  private animationId: number | null = null;
  
  constructor() {}
  
  ngOnInit(): void {
  }
  
  ngAfterViewInit(): void {
    if (typeof maplibregl !== 'undefined') {
      setTimeout(() => {
        this.initializeMap();
      }, 100);
    } else {
      this.loadMapLibreFromCDN();
    }
  }
  
  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }
  
  private loadMapLibreFromCDN(): void {
    const maplibreCSS = document.createElement('link');
    maplibreCSS.rel = 'stylesheet';
    maplibreCSS.href = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.css';
    document.head.appendChild(maplibreCSS);

    const maplibreJS = document.createElement('script');
    maplibreJS.src = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.js';
    maplibreJS.onload = () => {
      setTimeout(() => {
        this.initializeMap();
      }, 100);
    };
    document.head.appendChild(maplibreJS);
  }
  
  private initializeMap(): void {
    if (typeof maplibregl === 'undefined') {
      console.warn('MapLibre GL library is not loaded.');
      return;
    }

    try {
      this.map = new maplibregl.Map({
        container: this.mapContainer.nativeElement,
        style: this.style,
        center: this.center,
        zoom: this.zoom,
        pitch: this.pitch,
        bearing: this.bearing
      });
      if (this.navigationControl) {
        this.map.addControl(new maplibregl.NavigationControl(), 'top-right');
      }
      if (this.geolocateControl) {
        this.map.addControl(new maplibregl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          trackUserLocation: true
        }), 'top-right');
      }
      if (this.scaleControl) {
        this.map.addControl(new maplibregl.ScaleControl(), 'bottom-left');
      }
      if (this.fullscreenControl) {
        this.map.addControl(new maplibregl.FullscreenControl(), 'top-right');
      }

      this.map.on('load', () => {
        this.isMapInitialized = true;
        this.mapLoad.emit(this.map);
        this.addMarkers();
      });
      
      this.map.on('click', (e: any) => {
        this.mapClick.emit({
          lngLat: e.lngLat,
          point: e.point
        });
      });
      
      this.map.on('moveend', () => {
        const center = this.map.getCenter();
        const zoom = this.map.getZoom();
        this.mapMove.emit({
          center: [center.lng, center.lat],
          zoom: zoom
        });
      });
      
    } catch (error) {
      console.error('Error initializing MapLibre GL map:', error);
    }
  }
  
  private addMarkers(): void {
    this.mapMarkers.forEach(marker => marker.remove());
    this.mapMarkers.clear();
    
    this.markers.forEach(markerOptions => {
      const el = document.createElement('div');
      el.className = 'maplibre-marker';
      el.style.backgroundColor = markerOptions.color || '#3FB1CE';
      el.style.width = '32px';
      el.style.height = '32px';
      el.style.borderRadius = '50%';
      el.style.cursor = 'pointer';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.4)';
      el.style.transition = 'all 0.3s ease';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      const innerDot = document.createElement('div');
      innerDot.style.width = '8px';
      innerDot.style.height = '8px';
      innerDot.style.borderRadius = '50%';
      innerDot.style.backgroundColor = 'white';
      innerDot.style.opacity = '0.8';
      el.appendChild(innerDot);
      
      const marker = new maplibregl.Marker({
        element: el,
        draggable: markerOptions.draggable || false
      })
        .setLngLat([markerOptions.lng, markerOptions.lat]);
      if (markerOptions.popup) {
        const popup = new maplibregl.Popup({ offset: 25 })
          .setText(markerOptions.popup);
        marker.setPopup(popup);
      }
      el.addEventListener('click', () => {
        this.markerClick.emit(markerOptions);
      });
      
      marker.addTo(this.map);
      this.mapMarkers.set(markerOptions.id, marker);
    });
  }
  public flyTo(center: [number, number], zoom?: number): void {
    if (this.map) {
      this.map.flyTo({
        center: center,
        zoom: zoom || this.zoom,
        essential: true
      });
    }
  }
  
  public setZoom(zoom: number): void {
    if (this.map) {
      this.map.setZoom(zoom);
    }
  }
  
  public setBearing(bearing: number): void {
    if (this.map) {
      this.map.setBearing(bearing);
    }
  }
  
  public setPitch(pitch: number): void {
    if (this.map) {
      this.map.setPitch(pitch);
    }
  }
  
  public addMarker(markerOptions: MarkerOptions): void {
    this.markers.push(markerOptions);
    if (this.isMapInitialized) {
      this.addMarkers();
    }
  }
  
  public removeMarker(markerId: string): void {
    const marker = this.mapMarkers.get(markerId);
    if (marker) {
      marker.remove();
      this.mapMarkers.delete(markerId);
      this.markers = this.markers.filter(m => m.id !== markerId);
    }
  }
  
  public updateMarkerPosition(markerId: string, lng: number, lat: number): void {
    const marker = this.mapMarkers.get(markerId);
    if (marker) {
      marker.setLngLat([lng, lat]);
      const markerOptions = this.markers.find(m => m.id === markerId);
      if (markerOptions) {
        markerOptions.lng = lng;
        markerOptions.lat = lat;
      }
    }
  }
  
  public fitBounds(bounds: [[number, number], [number, number]], padding?: number): void {
    if (this.map) {
      this.map.fitBounds(bounds, {
        padding: padding || 50
      });
    }
  }
  
  public addLayer(layer: any, beforeId?: string): void {
    if (this.map && this.isMapInitialized) {
      this.map.addLayer(layer, beforeId);
    }
  }
  
  public removeLayer(layerId: string): void {
    if (this.map && this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
  }
  
  public addSource(sourceId: string, source: any): void {
    if (this.map && this.isMapInitialized) {
      this.map.addSource(sourceId, source);
    }
  }
  
  public removeSource(sourceId: string): void {
    if (this.map && this.map.getSource(sourceId)) {
      const style = this.map.getStyle();
      if (style && style.layers) {
        style.layers.forEach((layer: any) => {
          if (layer.source === sourceId) {
            this.map.removeLayer(layer.id);
          }
        });
      }
      this.map.removeSource(sourceId);
    }
  }
  
  public addGeoJSONLayer(id: string, data: any, style?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.map.addSource(id, {
      type: 'geojson',
      data: data
    });
    
    const layerStyle = style || {
      id: id,
      type: 'fill',
      source: id,
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.8
      }
    };
    
    this.map.addLayer(layerStyle);
  }
  
  public addHeatmapLayer(id: string, data: any, options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.map.addSource(id, {
      type: 'geojson',
      data: data
    });
    
    this.map.addLayer({
      id: id,
      type: 'heatmap',
      source: id,
      paint: {
        'heatmap-weight': options?.weight || 1,
        'heatmap-intensity': options?.intensity || 1,
        'heatmap-color': options?.color || [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(33,102,172,0)',
          0.2, 'rgb(103,169,207)',
          0.4, 'rgb(209,229,240)',
          0.6, 'rgb(253,219,199)',
          0.8, 'rgb(239,138,98)',
          1, 'rgb(178,24,43)'
        ],
        'heatmap-radius': options?.radius || 30
      }
    });
  }
  
  public addCircleLayer(id: string, data: any, options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.map.addSource(id, {
      type: 'geojson',
      data: data
    });
    
    this.map.addLayer({
      id: id,
      type: 'circle',
      source: id,
      paint: {
        'circle-radius': options?.radius || 6,
        'circle-color': options?.color || '#007cbf',
        'circle-stroke-color': options?.strokeColor || '#ffffff',
        'circle-stroke-width': options?.strokeWidth || 2
      }
    });
  }
  
  public addLineLayer(id: string, data: any, options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.map.addSource(id, {
      type: 'geojson',
      data: data
    });
    
    this.map.addLayer({
      id: id,
      type: 'line',
      source: id,
      layout: {
        'line-join': options?.join || 'round',
        'line-cap': options?.cap || 'round'
      },
      paint: {
        'line-color': options?.color || '#888',
        'line-width': options?.width || 3
      }
    });
  }
  
  public setLayerVisibility(layerId: string, visible: boolean): void {
    if (this.map && this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    }
  }
  
  public getMap(): any {
    return this.map;
  }
  
  public setMapStyle(styleType: 'light' | 'dark' | 'satellite'): void {
    if (!this.map || !this.isMapInitialized) return;
    
    let newStyle: any;
    
    switch (styleType) {
      case 'dark':
        newStyle = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
        break;
      case 'satellite':
        newStyle = {
          version: 8,
          sources: {
            'satellite': {
              type: 'raster',
              tiles: [
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256,
              attribution: '© Esri'
            }
          },
          layers: [{
            id: 'satellite-layer',
            type: 'raster',
            source: 'satellite'
          }]
        };
        break;
      case 'light':
      default:
        newStyle = this.style;
        break;
    }
    
    this.map.setStyle(newStyle);
    
    this.map.once('style.load', () => {
      this.addMarkers();
      if (this.activeWeatherLayer) {
        const tempLayer = this.activeWeatherLayer;
        this.activeWeatherLayer = null;
        this.toggleWeatherLayer(tempLayer);
      }
    });
  }
  
  public addGeoServerVectorLayer(options: {
    id: string;
    url: string;
    sourceLayer?: string;
    format?: 'mvt' | 'geojson' | 'topojson';
    style?: any;
  }): void {
    if (!this.map || !this.isMapInitialized) return;
    
    const { id, url, sourceLayer, format = 'mvt', style } = options;
    
    if (format === 'mvt') {
      this.map.addSource(`${id}-source`, {
        type: 'vector',
        tiles: [`${url}/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 14
      });
      
      const defaultStyle = style || {
        id: id,
        type: 'fill',
        source: `${id}-source`,
        'source-layer': sourceLayer || 'layer0',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.5,
          'fill-outline-color': '#1976D2'
        }
      };
      
      this.map.addLayer(defaultStyle);
      
    } else if (format === 'geojson') {
      this.map.addSource(`${id}-source`, {
        type: 'geojson',
        data: url
      });
      
      const defaultStyle = style || {
        id: id,
        type: 'fill',
        source: `${id}-source`,
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.5,
          'fill-outline-color': '#1976D2'
        }
      };
      
      this.map.addLayer(defaultStyle);
    }
  }
  
  public vectorDataToSVG(features: any[], options?: {
    width?: number;
    height?: number;
    style?: any;
  }): string {
    const width = options?.width || 1024;
    const height = options?.height || 1024;
    const bounds = this.map.getBounds();
    
    let svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">`;
    svg += '<defs>';
    
    svg += '<filter id="vector-blur"><feGaussianBlur stdDeviation="2"/></filter>';
    
    svg += `<filter id="vector-shadow">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="2" dy="2" result="offsetblur"/>
              <feFlood flood-color="#000000" flood-opacity="0.3"/>
              <feComposite in2="offsetblur" operator="in"/>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>`;
    
    svg += `<linearGradient id="gradient-green" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#81C784;stop-opacity:0.8" />
              <stop offset="100%" style="stop-color:#4CAF50;stop-opacity:0.8" />
            </linearGradient>`;
            
    svg += `<linearGradient id="gradient-blue" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#64B5F6;stop-opacity:0.8" />
              <stop offset="100%" style="stop-color:#2196F3;stop-opacity:0.8" />
            </linearGradient>`;
    
    svg += '</defs>';
    
    features.forEach((feature, index) => {
      const geometry = feature.geometry;
      const props = feature.properties || {};
      
      const fillColor = props.color || options?.style?.fill || '#2196F3';
      const opacity = props.opacity || options?.style?.opacity || 0.5;
      const strokeColor = props.strokeColor || options?.style?.strokeColor || '#1976D2';
      const strokeWidth = props.strokeWidth || options?.style?.strokeWidth || 2;
      const useBlur = options?.style?.blur !== false;
      const useShadow = options?.style?.shadow !== false;
      
      let fillValue = fillColor;
      if (fillColor.includes('76, 175, 80') || fillColor.includes('4CAF50')) {
        fillValue = 'url(#gradient-green)';
      } else if (fillColor.includes('33, 150, 243') || fillColor.includes('2196F3')) {
        fillValue = 'url(#gradient-blue)';
      }
      
      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        const coordinates = geometry.type === 'Polygon' 
          ? [geometry.coordinates]
          : geometry.coordinates;
          
        coordinates.forEach((polygon: number[][][]) => {
          let path = 'M';
          polygon[0].forEach((coord: number[], i: number) => {
            const point = this.map.project(coord);
            const x = (point.x / this.map.getCanvas().width) * width;
            const y = (point.y / this.map.getCanvas().height) * height;
            path += `${i === 0 ? '' : 'L'}${x},${y}`;
          });
          path += 'Z';
          
          svg += `<path d="${path}" 
                   fill="${fillValue}" 
                   fill-opacity="${fillValue.includes('url') ? '1' : opacity}" 
                   stroke="${strokeColor}" 
                   stroke-width="${strokeWidth}"
                   ${useShadow ? 'filter="url(#vector-shadow)"' : useBlur ? 'filter="url(#vector-blur)"' : ''}/>`;
        });
      } else if (geometry.type === 'Point') {
        const point = this.map.project(geometry.coordinates);
        const x = (point.x / this.map.getCanvas().width) * width;
        const y = (point.y / this.map.getCanvas().height) * height;
        const radius = props.radius || 5;
        
        svg += `<circle cx="${x}" cy="${y}" r="${radius}"
                 fill="${fillValue}" 
                 fill-opacity="${fillValue.includes('url') ? '1' : opacity}" 
                 stroke="${strokeColor}" 
                 stroke-width="${strokeWidth}"
                 ${useShadow ? 'filter="url(#vector-shadow)"' : useBlur ? 'filter="url(#vector-blur)"' : ''}/>`;
      } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        const lines = geometry.type === 'LineString' 
          ? [geometry.coordinates]
          : geometry.coordinates;
          
        lines.forEach((line: number[][]) => {
          let path = 'M';
          line.forEach((coord: number[], i: number) => {
            const point = this.map.project(coord);
            const x = (point.x / this.map.getCanvas().width) * width;
            const y = (point.y / this.map.getCanvas().height) * height;
            path += `${i === 0 ? '' : 'L'}${x},${y}`;
          });
          
          svg += `<path d="${path}" 
                   fill="none" 
                   stroke="${strokeColor}" 
                   stroke-width="${strokeWidth}"
                   stroke-opacity="${opacity}"
                   ${useBlur ? 'filter="url(#vector-blur)"' : ''}/>`;
        });
      }
    });
    
    svg += '</svg>';
    return svg;
  }
  
  public addSVGVectorLayer(id: string, features: any[], options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    const container = this.map.getContainer();
    const svgContainer = document.createElement('div');
    svgContainer.id = `svg-layer-${id}`;
    svgContainer.style.position = 'absolute';
    svgContainer.style.top = '0';
    svgContainer.style.left = '0';
    svgContainer.style.width = '100%';
    svgContainer.style.height = '100%';
    svgContainer.style.pointerEvents = 'none';
    svgContainer.style.zIndex = '2';
    
    const updateSVG = () => {
      const svgString = this.vectorDataToSVG(features, options);
      svgContainer.innerHTML = svgString;
    };
    
    updateSVG();
    
    const firstControl = container.querySelector('.maplibregl-control-container');
    if (firstControl) {
      container.insertBefore(svgContainer, firstControl);
    } else {
      container.appendChild(svgContainer);
    }
    
    this.map.on('move', updateSVG);
    this.map.on('zoom', updateSVG);
    
    if (!this.map._customSvgLayers) {
      this.map._customSvgLayers = {};
    }
    this.map._customSvgLayers[id] = {
      container: svgContainer,
      updateHandler: updateSVG
    };
  }
  
  public removeSVGVectorLayer(id: string): void {
    if (!this.map || !this.map._customSvgLayers || !this.map._customSvgLayers[id]) return;
    
    const layer = this.map._customSvgLayers[id];
    
    this.map.off('move', layer.updateHandler);
    this.map.off('zoom', layer.updateHandler);
    
    layer.container.remove();
        delete this.map._customSvgLayers[id];
  }
  
  public async loadGeoServerData(url: string, params?: {
    service?: string;
    version?: string;
    request?: string;
    typeName?: string;
    outputFormat?: string;
    srsName?: string;
  }): Promise<any> {
    const defaultParams = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      outputFormat: 'application/json',
      srsName: 'EPSG:4326',
      ...params
    };
    
    const queryString = Object.entries(defaultParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
      .join('&');
      
    const fullUrl = `${url}?${queryString}`;
    
    try {
      const response = await fetch(fullUrl);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error loading GeoServer data:', error);
      throw error;
    }
  }
  
  public addWeatherLayer(type: 'precipitation' | 'clouds' | 'temperature' = 'precipitation', opacity: number = 0.6): void {
    if (!this.map || !this.isMapInitialized) return;
    
    if (type === 'precipitation') {
      this.addSVGWeatherLayer();
    } else if (type === 'temperature') {
      console.warn('Temperature layer requires API key');
    } else if (type === 'clouds') {
      console.warn('Clouds layer requires API key');
    }
  }
  
  private addSVGWeatherLayer(): void {
    if (!this.map) return;
    
    const container = this.map.getContainer();
    const canvas = this.map.getCanvas();
    
    const svgContainer = document.createElement('div');
    svgContainer.className = 'weather-svg-container';
    svgContainer.style.position = 'absolute';
    svgContainer.style.top = '0';
    svgContainer.style.left = '0';
    svgContainer.style.width = '100%';
    svgContainer.style.height = '100%';
    svgContainer.style.pointerEvents = 'none';
    svgContainer.style.zIndex = '1';
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    
    svg.innerHTML = `
      <defs>
        <radialGradient id="precip1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" style="stop-color:rgb(33,150,243);stop-opacity:0.7" />
          <stop offset="50%" style="stop-color:rgb(33,150,243);stop-opacity:0.4" />
          <stop offset="100%" style="stop-color:rgb(33,150,243);stop-opacity:0" />
        </radialGradient>
        <radialGradient id="precip2" cx="50%" cy="50%" r="60%">
          <stop offset="0%" style="stop-color:rgb(21,101,192);stop-opacity:0.6" />
          <stop offset="70%" style="stop-color:rgb(21,101,192);stop-opacity:0.3" />
          <stop offset="100%" style="stop-color:rgb(21,101,192);stop-opacity:0" />
        </radialGradient>
        <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
        </filter>
      </defs>
    `;
    
    const precipitationAreas = [
      { lat: 41.35, lng: 69.15, radiusX: 8, radiusY: 6, gradient: 'precip1' },
      { lat: 41.28, lng: 69.32, radiusX: 10, radiusY: 7, gradient: 'precip2' },
      { lat: 41.31, lng: 69.24, radiusX: 7, radiusY: 5, gradient: 'precip1' },
      { lat: 41.25, lng: 69.38, radiusX: 6, radiusY: 4, gradient: 'precip2' },
      { lat: 41.33, lng: 69.20, radiusX: 9, radiusY: 6, gradient: 'precip1' }
    ];
    
    const precipGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    precipGroup.setAttribute('filter', 'url(#blur)');
    precipGroup.style.opacity = '0.8';
    
    const updateSVGPositions = () => {
      const bounds = this.map.getBounds();
      const zoom = this.map.getZoom();
      
      precipGroup.innerHTML = '';
      
      precipitationAreas.forEach((area, index) => {
        const point = this.map.project([area.lng, area.lat]);
        const canvasRect = canvas.getBoundingClientRect();
        
        const x = (point.x / canvasRect.width) * 100;
        const y = (point.y / canvasRect.height) * 100;
        
        const zoomScale = Math.pow(2, zoom - 13);
        const rx = area.radiusX * zoomScale;
        const ry = area.radiusY * zoomScale;
        
        const ellipse = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
        ellipse.setAttribute('cx', `${x}%`);
        ellipse.setAttribute('cy', `${y}%`);
        ellipse.setAttribute('rx', `${rx}%`);
        ellipse.setAttribute('ry', `${ry}%`);
        ellipse.setAttribute('fill', `url(#${area.gradient})`);
        
        const animateTransform = document.createElementNS('http://www.w3.org/2000/svg', 'animateTransform');
        animateTransform.setAttribute('attributeName', 'transform');
        animateTransform.setAttribute('type', 'translate');
        animateTransform.setAttribute('values', '0,0; 2,-1; 0,0');
        animateTransform.setAttribute('dur', `${15 + index * 2}s`);
        animateTransform.setAttribute('repeatCount', 'indefinite');
        
        ellipse.appendChild(animateTransform);
        precipGroup.appendChild(ellipse);
      });
    };
    
    svg.appendChild(precipGroup);
    svgContainer.appendChild(svg);
    
    const mapContainer = this.map.getContainer();
    const firstControl = mapContainer.querySelector('.maplibregl-control-container');
    if (firstControl) {
      mapContainer.insertBefore(svgContainer, firstControl);
    } else {
      mapContainer.appendChild(svgContainer);
    }
    
    updateSVGPositions();
    
    this.map.on('move', updateSVGPositions);
    this.map.on('zoom', updateSVGPositions);
    this.map.on('resize', updateSVGPositions);
    
    this.map._weatherSvgContainer = svgContainer;
    this.map._weatherSvgUpdateHandler = updateSVGPositions;
  }
  
  private addCanvasWeatherLayer(): void {
    if (!this.map) return;
    
    const canvas = document.createElement('canvas');
    const size = 1024;
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    
    const bounds = this.map.getBounds();
    
    const renderPrecipitation = () => {
      context.clearRect(0, 0, size, size);
      
      context.save();
      
      context.globalCompositeOperation = 'source-over';
      
      const time = Date.now() * 0.0005;
      
      const precipitationAreas = [
        { x: 0.3, y: 0.4, radiusX: 0.25, radiusY: 0.18, intensity: 0.8, speed: 0.3 },
        { x: 0.7, y: 0.3, radiusX: 0.3, radiusY: 0.2, intensity: 0.6, speed: 0.4 },
        { x: 0.5, y: 0.6, radiusX: 0.22, radiusY: 0.15, intensity: 0.7, speed: 0.35 },
        { x: 0.2, y: 0.7, radiusX: 0.18, radiusY: 0.12, intensity: 0.5, speed: 0.45 },
        { x: 0.8, y: 0.5, radiusX: 0.2, radiusY: 0.16, intensity: 0.65, speed: 0.38 }
      ];
      
      context.filter = 'blur(40px)';
      
      precipitationAreas.forEach((area, index) => {
        const offsetX = Math.sin(time * area.speed + index) * 0.05;
        const offsetY = Math.cos(time * area.speed * 0.8 + index) * 0.03;
        
        const centerX = (area.x + offsetX) * size;
        const centerY = (area.y + offsetY) * size;
        const radiusX = area.radiusX * size;
        const radiusY = area.radiusY * size;
        
        const gradient = context.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, Math.max(radiusX, radiusY)
        );
        
        const opacity = area.intensity * (0.6 + Math.sin(time * 2 + index * Math.PI / 3) * 0.3);
        
        gradient.addColorStop(0, `rgba(33, 150, 243, ${opacity})`);
        gradient.addColorStop(0.3, `rgba(33, 150, 243, ${opacity * 0.7})`);
        gradient.addColorStop(0.6, `rgba(21, 101, 192, ${opacity * 0.4})`);
        gradient.addColorStop(1, 'rgba(13, 71, 161, 0)');
        
        context.fillStyle = gradient;
        context.beginPath();
        context.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
        context.fill();
      });
      
      context.restore();
    };
    
    renderPrecipitation();
    
    this.map.addSource('weather-canvas', {
      type: 'canvas',
      canvas: canvas,
      coordinates: [
        [bounds.getWest(), bounds.getNorth()], 
        [bounds.getEast(), bounds.getNorth()], 
        [bounds.getEast(), bounds.getSouth()], 
        [bounds.getWest(), bounds.getSouth()]  
      ],
      animate: true
    });
    
    this.map.addLayer({
      id: 'weather-radar-layer',
      type: 'raster',
      source: 'weather-canvas',
      paint: {
        'raster-opacity': 0.7,
        'raster-fade-duration': 0
      }
    });
    
    this.map.on('moveend', () => {
      const newBounds = this.map.getBounds();
      const source = this.map.getSource('weather-canvas');
      if (source && source.type === 'canvas') {
        source.setCoordinates([
          [newBounds.getWest(), newBounds.getNorth()],
          [newBounds.getEast(), newBounds.getNorth()],
          [newBounds.getEast(), newBounds.getSouth()],
          [newBounds.getWest(), newBounds.getSouth()]
        ]);
      }
    });
    
    const animate = () => {
      renderPrecipitation();
      this.map.triggerRepaint();
      this.animationId = requestAnimationFrame(animate);
    };
    
    animate();
  }
  
  private animatePrecipitation(): void {
    if (!this.map || !this.map.getLayer('weather-radar-layer')) return;
    
    let opacity = 0.7;
    let direction = -0.05;
    
    const animate = () => {
      if (!this.map.getLayer('weather-radar-layer')) return;
      
      opacity += direction;
      if (opacity <= 0.3 || opacity >= 0.7) {
        direction = -direction;
      }
      
      this.map.setPaintProperty('weather-radar-layer', 'fill-opacity', opacity);
      
      this.animationId = requestAnimationFrame(animate);
    };
    
    animate();
  }
  
  private addWarningAreas(): void {
    if (!this.map) return;
    
    const warningAreas = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [69.1, 41.2],
              [69.3, 41.2],
              [69.3, 41.4],
              [69.1, 41.4],
              [69.1, 41.2]
            ]]
          },
          properties: {
            warning: 'heavy-rain',
            severity: 'high'
          }
        },
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [69.35, 41.25],
              [69.45, 41.25],
              [69.45, 41.35],
              [69.35, 41.35],
              [69.35, 41.25]
            ]]
          },
          properties: {
            warning: 'storm',
            severity: 'medium'
          }
        }
      ]
    };
    
    if (!this.map.getSource('warning-areas')) {
      this.map.addSource('warning-areas', {
        type: 'geojson',
        data: warningAreas
      });
    }
    
    if (!this.map.getLayer('warning-areas-layer')) {
      this.map.addLayer({
        id: 'warning-areas-layer',
        type: 'line',
        source: 'warning-areas',
        paint: {
          'line-color': '#FF0000',
          'line-width': 2,
          'line-dasharray': [2, 2]
        }
      });
    }
  }
  
  public addWMSLayer(id: string, url: string, layers: string, options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    const bbox = '{bbox-epsg-3857}';
    const wmsUrl = `${url}?service=WMS&version=1.1.0&request=GetMap&layers=${layers}&bbox=${bbox}&width=256&height=256&srs=EPSG:3857&format=image/png&transparent=true`;
    
    this.map.addSource(id, {
      type: 'raster',
      tiles: [wmsUrl],
      tileSize: 256,
      ...options
    });
    
    this.map.addLayer({
      id: `${id}-layer`,
      type: 'raster',
      source: id,
      paint: {
        'raster-opacity': options?.opacity || 0.7
      }
    });
  }
  
  public addTileLayer(id: string, tileUrl: string, options?: any): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.map.addSource(id, {
      type: 'raster',
      tiles: [tileUrl],
      tileSize: options?.tileSize || 256,
      attribution: options?.attribution || '',
      ...options
    });
    
    this.map.addLayer({
      id: `${id}-layer`,
      type: 'raster',
      source: id,
      paint: {
        'raster-opacity': options?.opacity || 1
      }
    }, options?.beforeId);
  }
  
  public toggleWeatherLayer(type: string): void {
    if (!this.map || !this.isMapInitialized) return;
    
    this.loadingWeatherLayer = true;
    
    if (this.activeWeatherLayer === type) {
      this.clearWeatherLayers();
      this.activeWeatherLayer = null;
      this.loadingWeatherLayer = false;
      return;
    }
    
    this.clearWeatherLayers();
    
    switch (type) {
      case 'precipitation-svg':
        this.addSVGWeatherLayer();
        break;
      case 'precipitation-canvas':
        this.addCanvasWeatherLayer();
        break;
      case 'temperature':
        break;
      case 'wind':
        break;
      case 'pressure':
        break;
      case 'clouds':
        break;
    }
    
    this.activeWeatherLayer = type;
    setTimeout(() => {
      this.loadingWeatherLayer = false;
    }, 1000);
  }
  
  private clearWeatherLayers(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    
    if (this.map._weatherSvgContainer) {
      if (this.map._weatherSvgUpdateHandler) {
        this.map.off('move', this.map._weatherSvgUpdateHandler);
        this.map.off('zoom', this.map._weatherSvgUpdateHandler);
        this.map.off('resize', this.map._weatherSvgUpdateHandler);
      }
      this.map._weatherSvgContainer.remove();
      this.map._weatherSvgContainer = null;
      this.map._weatherSvgUpdateHandler = null;
    }
    
    const weatherLayers = ['weather-radar-layer', 'temperature-layer', 'wind-layer', 'pressure-layer', 'clouds-layer', 'warning-areas-layer'];
    const weatherSources = ['weather-radar', 'weather-svg', 'weather-canvas', 'temperature-source', 'wind-source', 'pressure-source', 'clouds-source', 'warning-areas', 'precipitation-areas'];
    
    weatherLayers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    
    weatherSources.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    });
  }
}